import { Calendar } from "lucide-react";
import { useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useModels } from "@/hooks/api";
import { cn } from "@/lib/utils";

export function PredictionsSidebar({
  enabled,
  setEnabled,
  date,
  setDate,
  selected,
  onSelect,
}: {
  enabled: Record<string, boolean>;
  setEnabled: (next: Record<string, boolean>) => void;
  date: string;
  setDate: (d: string) => void;
  selected: string | null;
  onSelect: (slug: string) => void;
}) {
  const { data: models, isLoading } = useModels();

  // Default every model to enabled when the list first arrives.
  useEffect(() => {
    if (!models) return;
    const missing = models.filter((m) => !(m.slug in enabled));
    if (missing.length === 0) return;
    const next = { ...enabled };
    for (const m of missing) next[m.slug] = true;
    setEnabled(next);
  }, [models, enabled, setEnabled]);

  const list = (models ?? []).filter((m) => enabled[m.slug] ?? true);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border space-y-2">
        <div className="text-[10px] uppercase font-mono text-muted-foreground tracking-wider">
          Models
        </div>
        <div className="space-y-1.5">
          {isLoading && (
            <div className="text-[11px] text-muted-foreground">Loading…</div>
          )}
          {!isLoading && (models?.length ?? 0) === 0 && (
            <div className="text-[11px] text-muted-foreground">
              No model versions registered yet.
            </div>
          )}
          {models?.map((m) => (
            <div key={m.slug} className="flex items-center gap-2">
              <Checkbox
                id={`m-${m.slug}`}
                checked={enabled[m.slug] ?? true}
                onCheckedChange={(v) => setEnabled({ ...enabled, [m.slug]: !!v })}
              />
              <label
                htmlFor={`m-${m.slug}`}
                className="text-[12px] font-medium cursor-pointer truncate"
              >
                {m.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="p-2 border-b border-border space-y-1.5">
        <div className="text-[10px] uppercase font-mono text-muted-foreground tracking-wider">
          Prediction date
        </div>
        <div className="relative">
          <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="pl-7 h-7 text-[12px] font-mono"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-2 py-1.5 text-[10px] uppercase font-mono text-muted-foreground tracking-wider border-b border-border bg-muted/20">
          Predictions ({list.length})
        </div>
        {list.map((m) => {
          const isSel = m.slug === selected;
          const accuracyPct = m.accuracy === null || m.accuracy === undefined ? null : m.accuracy * 100;
          return (
            <button
              key={m.slug}
              onClick={() => onSelect(m.slug)}
              className={cn(
                "w-full text-left px-2 py-2 border-b border-border/60 hover:bg-accent/60 transition-colors block min-h-[44px] md:min-h-0",
                isSel && "bg-accent",
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono font-semibold text-[12px] truncate">{m.name}</div>
                <AccuracyBadge accuracy={m.accuracy} />
              </div>
              {m.description && (
                <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
                  {m.description}
                </div>
              )}
              <div className="flex items-center gap-2 mt-1 text-[9px] font-mono text-muted-foreground tabular-nums">
                <span>MAE {m.mae?.toFixed(2) ?? "—"}</span>
                <span>·</span>
                <span>F1 {m.f1?.toFixed(3) ?? "—"}</span>
                <span>·</span>
                <span>
                  {accuracyPct === null ? "—" : `${accuracyPct.toFixed(1)}% acc`}
                </span>
              </div>
            </button>
          );
        })}
        {!isLoading && list.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No models enabled.
          </div>
        )}
      </div>
    </div>
  );
}

function AccuracyBadge({ accuracy }: { accuracy: number | null | undefined }) {
  if (accuracy === null || accuracy === undefined) {
    return <Badge variant="outline">—</Badge>;
  }
  const pct = accuracy * 100;
  if (pct > 60)
    return (
      <Badge className="bg-[hsl(var(--chart-up))]/15 text-[hsl(var(--chart-up))] border-[hsl(var(--chart-up))]/30">
        {pct.toFixed(1)}%
      </Badge>
    );
  if (pct > 55)
    return (
      <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30">
        {pct.toFixed(1)}%
      </Badge>
    );
  return (
    <Badge className="bg-[hsl(var(--chart-down))]/15 text-[hsl(var(--chart-down))] border-[hsl(var(--chart-down))]/30">
      {pct.toFixed(1)}%
    </Badge>
  );
}
